import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import {
	Platform,
	TouchableOpacity,
	View,
} from 'react-native';
import moment from 'moment';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import Text from '../text/Text';
import PickerButton from '../picker/PickerButton/PickerButton';

const DATE_FORMAT = 'YYYY-MM-DD';

let DateTimePickerModal;
if (Platform.OS === 'web') {
	DateTimePickerModal = require('../DateTimePickerModal.web').default;
}
else {
	DateTimePickerModal = require('react-native-modal-datetime-picker').default;
}

const resolvePickerDate = (value) => {
	if (!value) {
		return new Date();
	}

	const strictDate = moment(value, DATE_FORMAT, true);
	if (strictDate.isValid()) {
		return strictDate.toDate();
	}

	const looseDate = moment(value);
	if (looseDate.isValid()) {
		return looseDate.toDate();
	}

	return new Date();
};

const normalizeMinimumDate = (value) => {
	if (!value) {
		return null;
	}

	const parsedDate = moment(value);
	if (!parsedDate.isValid()) {
		return null;
	}

	return parsedDate.startOf('day').toDate();
};

const DateSelectorInput = ({
	value,
	onChange,
	disabled = false,
	placeholder = 'Select date',
	clearLabel = 'Clear',
	minimumDate,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const [showDatePicker, setShowDatePicker] = useState(false);
	const resolvedMinimumDate = useMemo(() => normalizeMinimumDate(minimumDate), [minimumDate]);

	const selectedDate = useMemo(() => {
		const pickerDate = resolvePickerDate(value);
		if (!resolvedMinimumDate) {
			return pickerDate;
		}

		return moment(pickerDate).isBefore(resolvedMinimumDate, 'day')
			? resolvedMinimumDate
			: pickerDate;
	}, [resolvedMinimumDate, value]);

	const displayLabel = useMemo(() => {
		if (!value) {
			return placeholder;
		}

		const strictDate = moment(value, DATE_FORMAT, true);
		if (strictDate.isValid()) {
			return strictDate.format(DATE_FORMAT);
		}

		const looseDate = moment(value);
		if (looseDate.isValid()) {
			return looseDate.format(DATE_FORMAT);
		}

		return placeholder;
	}, [placeholder, value]);

	const openDatePicker = useCallback(() => {
		if (disabled) {
			return;
		}
		setShowDatePicker(true);
	}, [disabled]);

	const closeDatePicker = useCallback(() => {
		setShowDatePicker(false);
	}, []);

	const handleConfirm = useCallback((date) => {
		const nextDate = date ? moment(date).format(DATE_FORMAT) : '';
		onChange(nextDate);
		setShowDatePicker(false);
	}, [onChange]);

	const handleClear = useCallback(() => {
		onChange('');
	}, [onChange]);

	return (
		<View style={[styles.container, style]}>
			<View style={styles.row}>
				<PickerButton
					onPress={openDatePicker}
					disabled={disabled}
					style={styles.pickerButton}
				>
					{displayLabel}
				</PickerButton>
				{
					Boolean(value) && !disabled && (
						<TouchableOpacity
							onPress={handleClear}
							style={styles.clearButton}
							accessibilityRole="button"
						>
							<Text style={styles.clearText}>{clearLabel}</Text>
						</TouchableOpacity>
					)
				}
			</View>
			<DateTimePickerModal
				isVisible={showDatePicker}
				mode="date"
				date={selectedDate}
				minimumDate={resolvedMinimumDate}
				onConfirm={handleConfirm}
				onCancel={closeDatePicker}
			/>
		</View>
	);
};

const createStyles = ({ ui: { colors, space, touch, type } }) => ({
	container: {
		width: '100%',
	},
	row: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	pickerButton: {
		flex: 1,
	},
	// 44 pt accent text button.
	clearButton: {
		minHeight: touch.min,
		justifyContent: 'center',
		marginLeft: space.xs,
		paddingHorizontal: space.sm,
	},
	clearText: {
		...type.bodySmallStrong,
		color: colors.accent,
	},
});

export default DateSelectorInput;
